// DeepSeek 开放平台 · 用量信息页数据
// 数据窗口 = 演示账号各 Key 完整历史的并集：
//   2026-01-17（takt 创建的第三天）~ 2026-09-02（共 229 天），近 30 天即 8/4 ~ 9/2。
// 内置演示 Key「pdf识别」的真实接口快照峰（8/19、8/20）叠加在窗口内。

export interface DailyUsage {
  day: number; // 0 = 1/17
  label: string; // 1/17 ~ 9/2
  requests: number;
  tokens: number;
  cost: number; // CNY
}

export const USAGE_YEAR = 2026;

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** 数据窗口（takt 创建的第三天 2026-01-17 起，至最新调用日 2026-09-02） */
export const WINDOW_START: { month: number; day: number } = { month: 1, day: 17 };
export const WINDOW_END: { month: number; day: number } = { month: 9, day: 2 };

export interface UsageDayMeta {
  iso: string; // 2026-01-17（与本地 usage_daily.date 对齐）
  label: string; // 1/17
  month: number;
}

/** 窗口内每一天的元信息（升序） */
export const usageDays: UsageDayMeta[] = (() => {
  const list: UsageDayMeta[] = [];
  for (let m = WINDOW_START.month; m <= WINDOW_END.month; m++) {
    const firstDay = m === WINDOW_START.month ? WINDOW_START.day : 1;
    const lastDay = m === WINDOW_END.month ? WINDOW_END.day : MONTH_DAYS[m - 1];
    for (let d = firstDay; d <= lastDay; d++) {
      const iso = `${USAGE_YEAR}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      list.push({ iso, label: `${m}/${d}`, month: m });
    }
  }
  return list;
})();

/** 窗口终点所在自然月（用于「本月」筛选） */
export const windowEndMonth = usageDays[usageDays.length - 1].month;

// 真实接口快照：内置演示 Key「pdf识别」仅 8/19、8/20 两天有调用
const PEAKS: Record<string, { requests: number; tokens: number; cost: number }> = {
  "8/19": { requests: 18, tokens: 12388, cost: 0.0192698 },
  "8/20": { requests: 15, tokens: 3014795, cost: 5.9070717 },
};

export const dailyUsage: DailyUsage[] = usageDays.map((meta, day) => {
  const p = PEAKS[meta.label];
  return {
    day,
    label: meta.label,
    requests: p?.requests ?? 0,
    tokens: p?.tokens ?? 0,
    cost: p?.cost ?? 0,
  };
});

/** 内置演示 Key（pdf识别）全窗口合计（真实接口快照，5.9263415） */
export const totals = dailyUsage.reduce(
  (acc, d) => ({
    requests: acc.requests + d.requests,
    tokens: acc.tokens + d.tokens,
    cost: acc.cost + d.cost,
  }),
  { requests: 0, tokens: 0, cost: 0 }
);

export interface WalletSummary {
  currency: string;
  balance: number; // 充值余额（普通钱包）
  bonus: number; // 赠送余额
  totalCost: number; // 累计消费
}

/**
 * 演示账号（18038134223）顶部卡初始展示值 —— 与 data/app.db 中演示数据一致
 * （usage_daily 累计消费 2478370.99 + 内置演示 Key 5.9263415）。
 * 前端挂载后会通过 /api/wallet 以数据库实时值校准，二者保持相同。
 */
export const wallet: WalletSummary = {
  currency: "CNY",
  balance: 120323.08,
  bonus: 0,
  totalCost: 2478376.92,
};

export interface ApiKeyInfo {
  name: string;
  sensitiveId: string;
  created: string;
  lastUse: string;
}

export const apiKeys: ApiKeyInfo[] = [
  {
    name: "pdf识别",
    sensitiveId: "sk-86ce1***********************9d37",
    created: "2026-08-12 15:19:09",
    lastUse: "2026-08-20 16:17:43",
  },
];

export const models = [
  "deepseek-chat & deepseek-reasoner",
  "deepseek-v4-flash",
  "deepseek-v4-flash-vision-exp",
  "deepseek-v4-pro",
] as const;

export type ModelKey = (typeof models)[number];

// 各模型近 30 天汇总（真实接口数据；演示账号下全部调用归属 deepseek-v4-flash）
export const modelSummary: Record<ModelKey, { requests: number; tokens: number; cost: number }> = {
  "deepseek-chat & deepseek-reasoner": { requests: 0, tokens: 0, cost: 0 },
  "deepseek-v4-flash": { requests: 33, tokens: 3027183, cost: 5.9263415 },
  "deepseek-v4-flash-vision-exp": { requests: 0, tokens: 0, cost: 0 },
  "deepseek-v4-pro": { requests: 0, tokens: 0, cost: 0 },
};

export const formatCNY = (n: number) => `¥${n.toFixed(2)}`;
export const formatTokens = (n: number) => n.toLocaleString("en-US");

// ---- 时间范围筛选 ----

export type TimeRange = "近 7 天" | "近 30 天" | "本月" | "本年";

export const TIME_RANGES: TimeRange[] = ["近 7 天", "近 30 天", "本月", "本年"];

function monthOfLabel(label: string): number {
  return Number(label.split("/")[0]);
}

/** 按时间范围切出对应的日粒度数据（窗口终点为 9/2，本月 = 9/1 起，本年 = 全窗口） */
export function sliceByRange(range: TimeRange, data: DailyUsage[] = dailyUsage): DailyUsage[] {
  switch (range) {
    case "近 7 天":
      return data.slice(-7);
    case "近 30 天":
      return data.slice(-30);
    case "本月":
      return data.filter((d) => monthOfLabel(d.label) === windowEndMonth);
    case "本年":
      return data;
  }
}

export function sumUsage(rows: DailyUsage[]) {
  return rows.reduce(
    (acc, d) => ({
      requests: acc.requests + d.requests,
      tokens: acc.tokens + d.tokens,
      cost: acc.cost + d.cost,
    }),
    { requests: 0, tokens: 0, cost: 0 }
  );
}

export const modelNames = Object.keys(modelSummary) as ModelKey[];

// ---- 时区（影响用量日期显示）----

export interface TimezoneOption {
  id: string;
  label: string;
  /** UTC 偏移（分钟），GMT+8 = 480 */
  offset: number;
}

export const TIMEZONES: TimezoneOption[] = [
  { id: "Asia/Shanghai", label: "GMT+8 · 中国标准时间", offset: 480 },
  { id: "Asia/Tokyo", label: "GMT+9 · 日本标准时间", offset: 540 },
  { id: "Asia/Singapore", label: "GMT+8 · 新加坡时间", offset: 480 },
  { id: "UTC", label: "UTC+0 · 协调世界时", offset: 0 },
  { id: "Europe/London", label: "GMT+1 · 英国夏令时", offset: 60 },
  { id: "America/New_York", label: "GMT-5 · 美国东部时间", offset: -300 },
  { id: "America/Los_Angeles", label: "GMT-8 · 美国太平洋时间", offset: -480 },
];

export function timezoneById(id: string): TimezoneOption {
  return TIMEZONES.find((t) => t.id === id) ?? TIMEZONES[0];
}

/**
 * 将 GMT+8 的日期标签换算到目标时区显示。
 * 约定：每个数据日以 GMT+8 当日 12:00（= UTC 04:00）为代表时刻，
 * 换算到目标时区后落在哪天，标签就显示哪天。
 */
export function shiftLabel(label: string, targetOffset: number): string {
  const [m, d] = label.split("/").map(Number);
  const noonUtc = Date.UTC(USAGE_YEAR, m - 1, d) + 4 * 3600_000; // 12:00 GMT+8 对应的 UTC 时刻
  const local = new Date(noonUtc + (targetOffset - 480) * 60_000);
  return `${local.getUTCMonth() + 1}/${local.getUTCDate()}`;
}
